import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, ArrowRight, Loader2, FolderOpen } from "lucide-react";
import { useIntake } from "../context/IntakeContext";
import { LogoMark } from "../components/Logo";
import { PaymentOptions } from "../components/PaymentOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABEL = { letter: "Standard Package", full_petty: "Premium Package" };

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
    />
  </svg>
);

export const signInWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function CustomerDashboard() {
  const location = useLocation();
  const intake = useIntake();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [cases, setCases] = useState(null);

  const loadCases = async () => {
    try {
      const res = await fetch(`${API}/customer/cases`, { credentials: "include" });
      if (res.ok) setCases(await res.json());
    } catch (e) {
      setCases([]);
    }
  };

  useEffect(() => {
    if (location.state?.user) {
      loadCases();
      return;
    }
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API}/customer/me`, { credentials: "include" });
        if (!res.ok) throw new Error("Not authenticated");
        setUser(await res.json());
        await loadCases();
      } catch (e) {
        setUser(null);
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setCases(null);
    toast.success("Signed out.");
  };

  const [payOpen, setPayOpen] = useState(null);

  if (checking) {
    return (
      <main className="pt-[68px] min-h-[70vh] flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-[#2563EB]" />
      </main>
    );
  }

  if (!user) {
    return (
      <main data-testid="dashboard-signin" className="pt-[68px] min-h-[80vh] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-[2rem] p-10 text-center shadow-xl"
        >
          <LogoMark size={48} className="rounded-2xl mx-auto mb-6" />
          <h1 className="font-display font-semibold text-3xl mb-3">My Cases</h1>
          <p className="text-[#52525B] leading-relaxed mb-8">
            Sign in with Google to see every case you&rsquo;ve filed, its
            package, and where it stands.
          </p>
          <button
            data-testid="google-signin-button"
            onClick={signInWithGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-[#111827] text-white font-bold py-3.5 rounded-full hover:bg-[#FFFFFF] transition-colors"
          >
            <GoogleMark /> Continue with Google
          </button>
          <p className="text-xs text-[#a1a1aa] mt-5">
            We match your cases by the email on your Google account.
          </p>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="customer-dashboard" className="pt-[68px] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-12 h-12 rounded-full border-2 border-[#2563EB]"
                referrerPolicy="no-referrer"
              />
            ) : (
              <LogoMark size={48} className="rounded-full" />
            )}
            <div>
              <h1 className="font-display font-semibold text-3xl tracking-tight">
                Hi, {user.name?.split(" ")[0] || "there"}
              </h1>
              <p className="text-sm text-[#71717a]">{user.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="dashboard-start-case-button"
              onClick={() => intake.open("letter")}
              className="group flex items-center gap-2 bg-[#111827] text-[#FFFFFF] text-sm font-semibold pl-5 pr-1.5 py-1.5 rounded-full hover:bg-[#1F2937] transition-colors"
            >
              Start Your Case
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#2563EB] text-white">
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </button>
            <button
              data-testid="dashboard-signout-button"
              onClick={signOut}
              className="flex items-center gap-2 text-sm font-semibold border border-[#E5E7EB] bg-white px-4 py-2 rounded-full hover:border-[#111827] transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        {!cases ? (
          <p className="text-[#71717a]">Loading your cases...</p>
        ) : cases.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-3xl p-14 text-center">
            <FolderOpen size={44} className="text-[#2563EB] mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-display font-semibold text-2xl mb-2">No cases yet</p>
            <p className="text-[#52525B] max-w-sm mx-auto">
              Cases you file with {user.email} will show up here. Got someone
              who owes you money?
            </p>
            <button
              data-testid="dashboard-empty-start-button"
              onClick={() => intake.open("letter")}
              className="mt-7 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-8 py-3.5 rounded-full transition-colors"
            >
              Start Your Case
            </button>
          </div>
        ) : (
          <div data-testid="customer-cases-list" className="space-y-4">
            {cases.map((c, i) => (
              <motion.div
                key={c.reference}
                data-testid={`customer-case-${c.reference}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.06, 0.4) }}
                className="bg-white border border-[#E5E7EB] rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4"
              >
                <div>
                  <p className="font-display font-semibold text-xl">
                    {c.dispute_type}
                  </p>
                  <p className="text-sm text-[#71717a]">
                    Ref {c.reference} · {c.amount} ·{" "}
                    {new Date(c.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs font-bold px-3 py-1.5 rounded-full border bg-[#2563EB]/20 text-[#1D4ED8] border-[#2563EB]">
                    {PLAN_LABEL[c.plan] || c.plan}
                  </span>
                  <span
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                      c.payment_status === "paid"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-[#F3F4F6] text-[#71717a] border-[#E5E7EB]"
                    }`}
                  >
                    {c.payment_status === "paid" ? "Paid" : c.payment_status === "refunded" ? "Refunded" : "Unpaid"}
                  </span>
                  <span
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                      c.status === "handled"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-[#F3F4F6] text-[#71717a] border-[#E5E7EB]"
                    }`}
                  >
                    {c.status === "handled" ? "Handled" : "In progress"}
                  </span>
                  {c.payment_status === "unpaid" && (
                    <button
                      data-testid={`pay-case-${c.reference}`}
                      onClick={() => setPayOpen(payOpen === c.reference ? null : c.reference)}
                      className="text-xs font-bold px-4 py-1.5 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors"
                    >
                      Pay {c.plan === "full_petty" ? "$79" : "$39"}
                    </button>
                  )}
                </div>
                {payOpen === c.reference && (
                  <div
                    data-testid={`pay-options-${c.reference}`}
                    className="mt-4 border-t border-[#E5E7EB] pt-4"
                  >
                    <PaymentOptions
                      caseId={c.id}
                      lookupKey={c.plan === "full_petty" ? "go_full_petty" : "send_the_letter"}
                      priceLabel={c.plan === "full_petty" ? "$79" : "$39"}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
